import { API_URL } from "@/mainconfig";

const state = {
    bookingData: JSON.parse(sessionStorage.getItem("bookingData")) || null, // Ambil data dari sessionStorage jika ada
    error: null, // Untuk menyimpan error jika ada
    errorMessage: null, // Pesan error spesifik
    loading: false, // Status loading untuk operasi pemesanan
    bookingStatus: null, // Status pemesanan (e.g., success, pending, failed)
};

const getters = {
    isLoading: (state) => state.loading, // Apakah dalam status loading
    getBookingData: (state) => state.bookingData, // Data booking
    getBookingStatus: (state) => state.bookingStatus, // Status pemesanan
    getBookingError: (state) => state.errorMessage, // Pesan error pemesanan
};

const withExtendSourceFlow = (payload = {}) => {
    const data = payload && typeof payload === "object" ? { ...payload } : {};
    if (data.SourceFlow || data.sourceFlow) return data;
    const path = String(window?.location?.pathname || "").toLowerCase();
    if (path.includes("/exclusive")) {
        data.SourceFlow = "exclusive-link";
    } else {
        data.SourceFlow = "booking-detail";
    }
    return data;
};

const actions = {
    saveBookingData({ commit }, payload) {
        // Simpan data pemesanan ke state dan sessionStorage
        commit("setBookingData", payload);
    },

    async fetchBooking({ commit }, type) {
        commit("setLoading", true);
        try {
            const response = await API_URL.get(`/booking/ongoing`, {
                params: { type }, // Kirim type sebagai query parameter
            });

            // console.log("API Response:", response.data); // Debug: Cetak response dari backend

            const data = response.data?.data || []; // Cek apakah `data` ada di response
            // console.log("Filtered Data:", data); // Debug: Pastikan data valid

            commit("setBookingStatus", "success");
            commit("setError", null);
            return Array.isArray(data) ? data : []; // Kembalikan array kosong jika bukan array
        } catch (error) {
            console.error("API Error:", error.response?.data || error.message); // Debug: Log error
            commit("setBookingStatus", "failed");
            commit(
                "setError",
                error.response?.data?.msg ||
                    "An error occurred during booking fetch."
            );
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },

    async fetchOneBooking({ commit }, { bookingCode, params = {} }) {
        // Ambil detail pemesanan berdasarkan endpoint /booking/ongoing
        commit("setLoading", true);
        try {
            const response = await API_URL.get(
                `/booking/getOneBooking/${bookingCode}`,
                { params }
            );
            commit("setBookingData", response.data);
            commit("setError", null);
            return response.data;
        } catch (error) {
            commit(
                "setError",
                error.response?.data?.msg || "Failed to fetch booking details."
            );
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },

    async generatePin({ commit }, bookingDetailId) {
        commit("setLoading", true); // Aktifkan loading state
        try {
            const response = await API_URL.get(
                `/booking/generatePin/${bookingDetailId}`
            );

            console.log("Generated PIN response:", response.data);

            commit("setBookingStatus", "success");
            commit("setError", null);

            return response.data;
        } catch (error) {
            console.error(
                "Error generating PIN:",
                error.response?.data || error.message
            );

            commit("setBookingStatus", "failed");
            commit(
                "setError",
                error.response?.data?.msg ||
                    "Failed to generate PIN for booking detail."
            );

            throw error;
        } finally {
            commit("setLoading", false);
        }
    },

    async fetchBookingHistory({ commit }, { startDate, endDate }) {
        commit("setLoading", true); // Aktifkan status loading
        try {
            // Format parameter tanggal
            const formattedStartDate = startDate; // Sesuai format "24-01-2024"
            const formattedEndDate = endDate; // Sesuai format "24-01-2024"

            // Panggil endpoint API dengan parameter query
            const response = await API_URL.get("/booking/history", {
                params: {
                    startDate: formattedStartDate,
                    endDate: formattedEndDate,
                },
            });

            console.log("Booking History Response:", response.data); // Debug response

            const data = response.data?.data || []; // Pastikan data ada di response

            // Commit hasil sukses
            commit("setBookingStatus", "success");
            commit("setError", null);

            return Array.isArray(data) ? data : []; // Kembalikan array kosong jika bukan array
        } catch (error) {
            console.error(
                "Error fetching booking history:",
                error.response?.data || error.message
            );

            // Commit jika terjadi error
            commit("setBookingStatus", "failed");
            commit(
                "setError",
                error.response?.data?.msg || "Failed to fetch booking history."
            );

            throw error; // Lempar error jika perlu ditangani di komponen
        } finally {
            commit("setLoading", false); // Nonaktifkan status loading
        }
    },

    async cleaningRoomRequest({ commit }, payload) {
        commit("setLoading", true); // Set loading ke true
        try {
            const response = await API_URL.post("/cleaningRequest", payload);
            console.log("Cleaning Room Request Success:", response.data);

            // Update state jika diperlukan
            commit("setError", null);

            return response; // Kembalikan data response jika diperlukan
        } catch (error) {
            console.error(
                "Error sending cleaning request:",
                error.response?.data || error.message
            );

            commit(
                "setError",
                error.response?.data?.msg || "Failed to send cleaning request."
            );

            throw error; // Lempar error untuk ditangani di komponen
        } finally {
            commit("setLoading", false); // Set loading ke false
        }
    },

    async cancelCleaningRoomRequest({ commit }, bookDetailId) {
        commit("setLoading", true); // Set loading ke true
        try {
            const response = await API_URL.get(
                `/cleaningRequest/reject/${bookDetailId}`
            );
            console.log("Cancel Cleaning Request Success:", response.data);

            // Update state jika diperlukan
            commit("setError", null);

            return response; // Kembalikan data response jika diperlukan
        } catch (error) {
            console.error(
                "Error canceling cleaning request:",
                error.response?.data || error.message
            );

            commit(
                "setError",
                error.response?.data?.msg ||
                    "Failed to cancel cleaning request."
            );

            throw error; // Lempar error untuk ditangani di komponen
        } finally {
            commit("setLoading", false); // Set loading ke false
        }
    },

    async fetchCleaningRequests({ commit }, bookDetailId) {
        commit("setLoading", true); // Set loading ke true
        try {
            const response = await API_URL.get(
                `/cleaningRequest/getAll/${bookDetailId}`
            );
            console.log("Fetched Cleaning Requests:", response.data);

            // Update state jika diperlukan
            commit("setError", null);

            return response.data; // Kembalikan data response jika diperlukan
        } catch (error) {
            console.error(
                "Error fetching cleaning requests:",
                error.response?.data || error.message
            );

            commit(
                "setError",
                error.response?.data?.msg ||
                    "Failed to fetch cleaning requests."
            );

            throw error; // Lempar error untuk ditangani di komponen
        } finally {
            commit("setLoading", false); // Set loading ke false
        }
    },

    async generateNewPinRoom({ commit }, bookingDetailId) {
        commit("setLoading", true); // Aktifkan loading state
        try {
            const response = await API_URL.get(
                `/booking/generatePin/${bookingDetailId}`
            );

            console.log("Generated PIN response:", response.data);

            commit("setError", null);

            return response.data; // Kembalikan data PIN yang dihasilkan
        } catch (error) {
            console.error(
                "Error generating PIN:",
                error.response?.data || error.message
            );

            commit(
                "setError",
                error.response?.data?.msg ||
                    "Failed to generate PIN for booking detail."
            );

            throw error; // Lempar error untuk ditangani di komponen
        } finally {
            commit("setLoading", false); // Nonaktifkan loading state
        }
    },

    async downloadReceipt(_, { bookingCode, params = {} }) {
        try {
            console.log("Booking Code Received:", bookingCode);
            console.log("Params Received:", params);

            // Validasi bookingCode harus string
            if (!bookingCode || typeof bookingCode !== "string") {
                throw new Error(
                    "Invalid bookingCode. It must be a non-empty string."
                );
            }

            // Fetch PDF content
            const response = await API_URL.get(
                `/booking/getBookingInvoice/${bookingCode}`,
                {
                    responseType: "arraybuffer",
                    params, // Kirim params (termasuk key jika ada)
                }
            );

            console.log("Receipt fetched successfully:", response);

            // Convert binary response to Blob
            const blob = new Blob([response.data], { type: "application/pdf" });

            // Generate temporary download URL
            const downloadUrl = window.URL.createObjectURL(blob);

            // Create file name
            const fileName = `BookingInvoice-${bookingCode}.pdf`;

            // Trigger download
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();

            // Clean up
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error("Failed to download receipt:", error);
            throw error; // Lempar error untuk ditangani di komponen
        }
    },

    /** Daftar transaksi pembayaran satu booking (awal + reschedule/change room/add-on). */
    async fetchBookingPayments(_, { bookingCode, params = {} }) {
        const response = await API_URL.get(
            `/booking/getBookingPayments/${bookingCode}`,
            { params }
        );
        return response?.data?.data || [];
    },

    /** Invoice satu transaksi. Payment modify menumpang pada Book yang sama, jadi kuncinya PaymentId. */
    async downloadPaymentInvoice(_, { paymentId, fileName, params = {} }) {
        const response = await API_URL.get(
            `/booking/getPaymentInvoice/${paymentId}`,
            { responseType: "arraybuffer", params }
        );

        const blob = new Blob([response.data], { type: "application/pdf" });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = fileName || `Invoice-${paymentId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
    },

    clearBookingData({ commit }) {
        // Bersihkan data pemesanan dari state dan sessionStorage
        commit("clearBookingData");
    },
    async confirmBooking({ commit }, data) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post(
                `/booking/insertInfoExternalBooking`,
                data
            );

            return response.data;
        } catch (error) {
            console.error(
                "Error confirmBooking:",
                error.response?.data || error.message
            );

            throw error;
        } finally {
            commit("setLoading", false);
        }
    },
    async confirmReservation({ commit }, data) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post(
                `/booking/confirmExternalReservation`,
                data
            );

            return response.data;
        } catch (error) {
            console.error(
                "Error confirmBooking:",
                error.response?.data || error.message
            );

            throw error;
        } finally {
            commit("setLoading", false);
        }
    },

    async getOneRoom({ commit }, roomId) {
        // Ambil detail pemesanan berdasarkan endpoint /booking/ongoing
        commit("setLoading", true);
        try {
            const response = await API_URL.get(`/room/${roomId}`);
            commit("setError", null);
            return response.data;
        } catch (error) {
            commit(
                "setError",
                error.response?.data?.msg || "Failed to fetch booking details."
            );
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },

    async getOneRoomBySlug({ commit }, slug) {
        commit("setLoading", true);
        try {
            const response = await API_URL.get(`/room/slug/${slug}`);
            commit("setError", null);
            return response.data;
        } catch (error) {
            commit(
                "setError",
                error.response?.data?.msg || "Failed to fetch booking details."
            );
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },

    // CH-W18 - Reschedule actions (SourceFlow auto: exclusive-link bila di halaman /exclusive)
    async rescheduleSimulation({ commit }, data) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post(`/booking/modify/reschedule/simulation`, withExtendSourceFlow(data));
            commit("setError", null);
            return response.data;
        } catch (error) {
            commit("setError", error.response?.data?.msg || "Failed to simulate reschedule.");
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },
    async rescheduleConfirm({ commit }, data) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post(`/booking/modify/reschedule/confirm`, withExtendSourceFlow(data));
            commit("setError", null);
            return response.data;
        } catch (error) {
            commit("setError", error.response?.data?.msg || "Failed to confirm reschedule.");
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },
    async reschedulePaymentCreate({ commit }, data) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post(`/booking/modify/reschedule/payment/create`, withExtendSourceFlow(data));
            commit("setError", null);
            return response.data;
        } catch (error) {
            commit("setError", error.response?.data?.msg || "Failed to create reschedule payment.");
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },
    async reschedulePaymentStatus({ commit }, data) {
        try {
            const response = await API_URL.get(`/booking/modify/reschedule/payment-status`, { params: data });
            return response.data;
        } catch (error) {
            commit("setError", error.response?.data?.msg || "Failed to get reschedule payment status.");
            throw error;
        }
    },
    async rescheduleEffectivePolicy({ commit }, data) {
        try {
            const response = await API_URL.get(`/booking/modify/reschedule-policy/effective`, { params: withExtendSourceFlow(data) });
            return response.data;
        } catch (error) {
            commit("setError", error.response?.data?.msg || "Failed to fetch reschedule policy.");
            throw error;
        }
    },
    async rescheduleCancelIntent({ commit }, data) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post(`/booking/modify/reschedule/cancel-intent`, withExtendSourceFlow(data));
            commit("setError", null);
            return response.data;
        } catch (error) {
            commit("setError", error.response?.data?.msg || "Failed to cancel reschedule.");
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },

    // CH-W08 - Cancellation (full-booking). Effective policy + cancel. Refund di luar sistem.
    async cancellationEffectivePolicy({ commit }, data) {
        try {
            const response = await API_URL.get(`/booking/modify/cancellation-policy/effective`, { params: withExtendSourceFlow(data) });
            return response.data;
        } catch (error) {
            commit("setError", error.response?.data?.msg || "Failed to fetch cancellation policy.");
            throw error;
        }
    },
    async modifyCancel({ commit }, data) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post(`/booking/modify/cancel`, withExtendSourceFlow(data));
            commit("setError", null);
            return response.data;
        } catch (error) {
            commit("setError", error.response?.data?.msg || "Failed to cancel booking.");
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },
    async cancellationLinkResolve(_ctx, { token }) {
        const response = await API_URL.get(`/booking/modify/cancellation-link/resolve`, { params: { token } });
        return response.data;
    },
    async cancellationRequestCreate({ commit }, data) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post(`/booking/cancellation/request`, withExtendSourceFlow(data));
            commit("setError", null);
            return response.data;
        } catch (error) {
            commit("setError", error.response?.data?.msg || "Failed to submit cancellation request.");
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },
    async cancellationRequestList(_ctx, data) {
        const response = await API_URL.get(`/booking/cancellation/requests`, { params: withExtendSourceFlow(data) });
        return response.data;
    },
    async cancellationRequestCancel({ commit }, data) {
        const response = await API_URL.post(`/booking/cancellation/request/cancel`, withExtendSourceFlow(data));
        commit("setError", null);
        return response.data;
    },

    // CH-W25 - Change Room (customer/exclusive = request-only; agent finalisasi). Plus pending-payment "Pay Now".
    async changeRoomEffectivePolicy({ commit }, data) {
        try {
            const response = await API_URL.get(`/booking/modify/change-room-policy/effective`, { params: withExtendSourceFlow(data) });
            return response.data;
        } catch (error) {
            commit("setError", error.response?.data?.msg || "Failed to fetch change room policy.");
            throw error;
        }
    },
    async changeRoomRequestCreate({ commit }, data) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post(`/booking/change-room/request`, withExtendSourceFlow(data));
            commit("setError", null);
            return response.data;
        } catch (error) {
            commit("setError", error.response?.data?.msg || "Failed to submit change room request.");
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },
    async changeRoomRequestList({ commit }, data) {
        try {
            const response = await API_URL.get(`/booking/change-room/requests`, { params: data });
            return response.data;
        } catch (error) {
            commit("setError", error.response?.data?.msg || "Failed to load change room requests.");
            throw error;
        }
    },
    async changeRoomRequestCancel({ commit }, data) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post(`/booking/change-room/request/cancel`, data);
            commit("setError", null);
            return response.data;
        } catch (error) {
            commit("setError", error.response?.data?.msg || "Gagal membatalkan pengajuan.");
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },
    async changeRoomPaymentCreate({ commit }, data) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post(`/booking/modify/room/payment/create`, withExtendSourceFlow(data));
            commit("setError", null);
            return response.data;
        } catch (error) {
            commit("setError", error.response?.data?.msg || "Failed to create change room payment.");
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },
    async changeRoomPaymentStatus({ commit }, data) {
        try {
            const response = await API_URL.get(`/booking/modify/room/payment-status`, { params: data });
            return response.data;
        } catch (error) {
            commit("setError", error.response?.data?.msg || "Failed to get change room payment status.");
            throw error;
        }
    },
    async changeRoomCancelIntent({ commit }, data) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post(`/booking/modify/room/cancel-intent`, withExtendSourceFlow(data));
            commit("setError", null);
            return response.data;
        } catch (error) {
            commit("setError", error.response?.data?.msg || "Failed to cancel change room.");
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },

    async initExtendData({ commit }, data) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post(`/booking/initExtend`, withExtendSourceFlow(data));
            commit("setError", null);
            return response.data;
        } catch (error) {
            commit(
                "setError",
                error.response?.data?.msg || "Failed to fetch init extend."
            );
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },
    async createExtend({ commit }, data) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post(`/booking/createExtend`, withExtendSourceFlow(data));
            commit("setError", null);
            return response.data;
        } catch (error) {
            commit(
                "setError",
                error.response?.data?.msg || "Failed to fetch create extend."
            );
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },
    async cancelExtend({ commit }, data) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post(`/booking/cancelExtend`, data);
            commit("setError", null);
            return response.data;
        } catch (error) {
            commit(
                "setError",
                error.response?.data?.msg || "Failed to fetch cancel extend."
            );
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },
    async confirmExtendOTA({ commit }, data) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post(
                `/booking/confirmExtendOTA`,
                data
            );
            commit("setError", null);
            return response.data;
        } catch (error) {
            commit(
                "setError",
                error.response?.data?.msg || "Failed to confirm extend OTA."
            );
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },
    async applyVoucherExtend({ commit }, data) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post(
                `/booking/applyVoucherExtend`,
                data
            );
            commit("setError", null);
            return response.data;
        } catch (error) {
            commit(
                "setError",
                error.response?.data?.msg || "Failed to apply voucher extend."
            );
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },
    async removeVoucherExtend({ commit }, data) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post(
                `/booking/removeVoucherExtend`,
                data
            );
            commit("setError", null);
            return response.data;
        } catch (error) {
            commit(
                "setError",
                error.response?.data?.msg || "Failed to remove voucher extend."
            );
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },
    async getBookingBySlug({ commit }, payload) {
        commit("setLoading", true);
        try {
            const slug = typeof payload === "string" ? payload : payload?.slug;
            const params = typeof payload === "object" ? payload?.params || {} : {};
            const response = await API_URL.get(`/booking/getBookingBySlug/${slug}`, { params });
            commit("setError", null);
            return response.data;
        } catch (error) {
            commit(
                "setError",
                error.response?.data?.msg || "Failed to fetch booking details."
            );
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },
    async getExclusiveLinkBySlug({ commit }, slug) {
        commit("setLoading", true);
        try {
            const response = await API_URL.get(`/exclusiveLink/by-slug/${slug}`);
            commit("setError", null);
            return response.data;
        } catch (error) {
            commit(
                "setError",
                error.response?.data?.msg || "Failed to fetch exclusive link."
            );
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },
};

const mutations = {
    setBookingData(state, payload) {
        // Set data pemesanan ke state dan simpan ke sessionStorage
        state.bookingData = payload;
        sessionStorage.setItem("bookingData", JSON.stringify(payload));
    },
    clearBookingData(state) {
        // Hapus data pemesanan dari state dan sessionStorage
        state.bookingData = null;
        state.bookingStatus = null;
        sessionStorage.removeItem("bookingData");
    },
    setLoading(state, loading) {
        // Set status loading
        state.loading = loading;
    },
    setBookingStatus(state, status) {
        // Set status pemesanan
        state.bookingStatus = status;
    },
    setError(state, error) {
        // Set error jika terjadi masalah
        state.error = error;
        state.errorMessage = error || "An error occurred.";
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
